import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { AIConfigHistory } from '@octery/shared';

export function ConfigHistoryCard() {
  const { data: history = [], isLoading } = useQuery({
    queryKey: ['ai-settings', 'history'],
    queryFn: () => api.get<AIConfigHistory[]>('/api/ai-settings/history'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Change History</CardTitle>
        <CardDescription>Recent changes to your AI configuration</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No changes recorded yet
          </p>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-auto">
            {history.map((entry) => (
              <div key={entry.id} className="border rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">
                    {new Date(entry.changedAt).toLocaleString()}
                  </span>
                  <span className="text-xs bg-muted px-2 py-0.5 rounded">
                    {Object.keys(entry.changes).length} field
                    {Object.keys(entry.changes).length !== 1 ? 's' : ''} changed
                  </span>
                </div>
                <div className="space-y-1">
                  {Object.entries(entry.changes).map(([field, diff]) => (
                    <div key={field} className="flex gap-2 items-start">
                      <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded shrink-0">
                        {field}
                      </span>
                      <div className="text-xs text-muted-foreground min-w-0">
                        <span className="line-through text-destructive/70">
                          {typeof diff.old === 'string'
                            ? diff.old.slice(0, 60) + (String(diff.old).length > 60 ? '...' : '')
                            : JSON.stringify(diff.old)}
                        </span>
                        <span className="mx-1">→</span>
                        <span className="text-green-700">
                          {typeof diff.new === 'string'
                            ? diff.new.slice(0, 60) + (String(diff.new).length > 60 ? '...' : '')
                            : JSON.stringify(diff.new)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
